import React, { useEffect, useRef } from "react";
import {
  AREA_PADRAO_PAVER_MM2,
  DIAMETRO_PADRAO_PAVER_MM,
  calcularCorposProva,
  calcularIdadeDias,
  calcularResumo,
  formatarDataBR,
  numeroBR,
} from "./paverCalculos";
import "./RelatorioRupturaPaverPrint.css";
import RelatorioCalibracaoPrensaPrint from "../CalibracaoPrensa/RelatorioCalibracaoPrensaPrint";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function imprimirEmRetrato() {
  const styleId = "relatorio-paver-print-orientacao";
  document.getElementById(styleId)?.remove();

  const style = document.createElement("style");
  style.id = styleId;
  style.media = "print";
  style.textContent = `
    @page relatorioPaver {
      size: A4 portrait;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
  window.print();
}

function valor(valor, fallback = "-") {
  return valor || fallback;
}

function dividirEmPaginas(lista, quantidade) {
  const paginas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    paginas.push(lista.slice(i, i + quantidade));
  }
  return paginas;
}

function dividirEmLinhas(lista, quantidade) {
  const linhas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    linhas.push(lista.slice(i, i + quantidade));
  }
  return linhas;
}

function CabecalhoRelatorioPaver({ dataRelatorio, codigo }) {
  return (
    <>
      <table className="cabecalho">
        <tbody>
          <tr>
            <td rowSpan="3" style={{ width: 140 }}>
              <LogoRelatorio alt="Logo" className="logo"  />
            </td>
            <td rowSpan="3" className="titulo-principal">
              SISTEMA DE GESTÃO DA QUALIDADE
              <br />
              Determinação de Resistência à compressão de peças de
              <br />
              concreto para pavimento intertravado (PAVER&apos;S)
            </td>
            <td style={{ width: 95 }}>RQ 2.2.15</td>
          </tr>
          <tr>
            <td>REVISÃO 00</td>
          </tr>
          <tr>
            <td>20/08/2025</td>
          </tr>
        </tbody>
      </table>

      <div className="linha-info">
        <span>
          <strong>DATA</strong>&nbsp;&nbsp; {formatarDataBR(dataRelatorio)}
        </span>
        <span>
          <strong>CÓDIGO:</strong> {valor(codigo, "CE-RE-PE-000/2025")}
        </span>
      </div>
    </>
  );
}

function LinhaFinalPaginaPaver() {
  return (
    <div className="rodape-restrito">
      <div className="linha-final-pagina" aria-hidden="true" />
      <div className="texto-rodape-restrito">
        DOCUMENTO PARA USO RESTRITO. REPRODUÇÃO/CÓPIA PROIBIDA SEM AUTORIZAÇÃO DA EMPRESA
      </div>
    </div>
  );
}

function AssinaturaResponsavelTecnico() {
  return (
    <div className="assinatura-tecnica">
      <img src="/Images/assinaturaLeal.png" alt="Assinatura" />
      <div className="linha-assinatura" />
      <strong>José Carlos Lemes Leal</strong>
      <span>Eng. Civil Esp. CREA 203379/D-TO</span>
    </div>
  );
}

function RelatorioFotografico({ ensaio, fotos = [] }) {
  if (!fotos.length) return null;

  return dividirEmPaginas(fotos, 6).map((paginaFotos, paginaIndex) => (
    <div className="pagina pagina-fotografica" key={`pagina-fotos-${paginaIndex}`}>
      <CabecalhoRelatorioPaver dataRelatorio={ensaio?.dataRelatorio} codigo={ensaio?.codigo} />

      <div className="faixa-relatorio-fotografico">RELATÓRIO FOTOGRÁFICO</div>

      <div className="grade-fotografica">
        {dividirEmLinhas(paginaFotos, 2).map((linhaFotos, linhaIndex) => (
          <div className="linha-fotografica-bloco" key={`linha-fotos-${paginaIndex}-${linhaIndex}`}>
            <div className="linha-fotografica">
              {linhaFotos.map((foto, index) => {
                const fotoNumero = paginaIndex * 6 + linhaIndex * 2 + index + 1;

                return (
                  <div className="foto-relatorio-item" key={foto.id || foto.url || `${paginaIndex}-${fotoNumero}`}>
                    <div className="foto-relatorio-imagem">
                      <img src={foto.url} alt={foto.legenda || `Foto ${fotoNumero}`} />
                    </div>
                    <div className="foto-relatorio-legenda">
                      {foto.legenda || `FOTO ${String(fotoNumero).padStart(2, "0")}`}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="linha-azul-fotos" aria-hidden="true" />
          </div>
        ))}
      </div>

      <LinhaFinalPaginaPaver />
    </div>
  ));
}

export default function RelatorioRupturaPaverPrint({
  ensaio,
  fotosRelatorio = [],
  onVoltar,
  autoPrint = false,
}) {
  const jaImprimiuRef = useRef(false);
  useNomeArquivoPdf(ensaio?.codigo);
  const corposProva = calcularCorposProva(
    ensaio?.corposProva || [],
    ensaio?.areaCorpoProva || AREA_PADRAO_PAVER_MM2
  );
  const resumo = calcularResumo(corposProva);
  const diametroCorpoProva = ensaio?.diametroCorpoProva || DIAMETRO_PADRAO_PAVER_MM;
  const dataRelatorio = ensaio?.dataRelatorio;
  const idade = calcularIdadeDias(ensaio?.dataFabricacao, ensaio?.dataEnsaio);
  const temIdade =
    Boolean(ensaio?.dataFabricacao && ensaio?.dataEnsaio) && idade !== "";
  const temFpkEstimado =
    resumo.fpkEstimado !== "" &&
    resumo.fpkEstimado !== null &&
    resumo.fpkEstimado !== undefined;

  useEffect(() => {
    if (!autoPrint || jaImprimiuRef.current) return;

    jaImprimiuRef.current = true;
    const frame = window.requestAnimationFrame(imprimirEmRetrato);
    return () => window.cancelAnimationFrame(frame);
  }, [autoPrint]);

  if (!ensaio) return null;

  return (
    <div className="relatorio-paver-print">
      {onVoltar && (
        <div className="acoes-print">
          <button className="btn-voltar" onClick={onVoltar}>
            Voltar
          </button>
          <button className="btn-imprimir" onClick={imprimirEmRetrato}>
            Imprimir
          </button>
        </div>
      )}

      <div className="pagina">
        <CabecalhoRelatorioPaver dataRelatorio={dataRelatorio} codigo={ensaio.codigo} />

        <div className="dados-cliente">
          <div>
            <strong>EMPRESA SOLICITANTE:</strong>
            {valor(ensaio.cliente)}
          </div>
          <div>
            <strong>ENDEREÇO:</strong>
            {valor(ensaio.endereco)}
          </div>
          <div>
            <strong>LOCAL DA APLICAÇÃO:</strong>
            {valor(ensaio.localAplicacao || ensaio.local)}
          </div>
        </div>

        <div className="secao">1. ENSAIO REALIZADO</div>
        <div className="texto">
          Determinação de Resistência à compressão de peças de concreto (PAVER&apos;S)
        </div>

        <div className="secao">2. IDENTIFICAÇÃO DA AMOSTRA</div>
        <table className="identificacao">
          <tbody>
            <tr>
              <td>Característica do piso:</td>
              <td>{valor(ensaio.caracteristicaPiso)}</td>
            </tr>
            <tr>
              <td>Procedência:</td>
              <td>{valor(ensaio.procedencia || ensaio.cliente)}</td>
            </tr>
            <tr>
              <td>Lote:</td>
              <td>{valor(formatarDataBR(ensaio.lote), valor(ensaio.lote))}</td>
            </tr>
            <tr>
              <td>Local de aplicação:</td>
              <td>{valor(ensaio.localAplicacao || ensaio.local)}</td>
            </tr>
            <tr>
              <td>Dimensões (Comp x Larg x Alt.) mm:</td>
              <td>{valor(ensaio.dimensoesDeclaradas)}</td>
            </tr>
            <tr>
              <td>Resistência esperada:</td>
              <td>
                {ensaio.resistenciaEsperadaNaoInformada
                  ? "Não informada"
                  : valor(ensaio.resistenciaEsperada)}
              </td>
            </tr>
            <tr>
              <td>Formato tipo:</td>
              <td>{valor(ensaio.formatoTipo)}</td>
            </tr>
          </tbody>
        </table>

        <div className="secao">3. METODOLOGIA</div>
        <div className="texto">
          Os ensaios seguiram as prescrições da norma:
          <br />
          NBR-9781 - Peças de concreto para pavimentação.
        </div>

        <div className="secao">4. DADOS DO ENSAIO</div>
        <div className="dados-ensaio">
          <div>Amostras:</div>
          <div>
            {corposProva.length} Paver&apos;s para ruptura por compressão axial.
            Peças capeadas com cimento.
          </div>
          <div className="dados-ensaio-grid">
            <span>
              Data da fabricação:{" "}
              {ensaio.dataFabricacaoNaoInformada
                ? "Não informada"
                : formatarDataBR(ensaio.dataFabricacao)}
            </span>
            <span>Data do ensaio: {formatarDataBR(ensaio.dataEnsaio)}</span>
            <span>
              Idade declarada: {temIdade ? `${idade} dias` : "-"}
            </span>
          </div>
        </div>

        <div className="quadro-titulo">
          <strong>Quadro 1</strong> - Resultados ensaio de Resistência à Compressão Axial.
        </div>
        <table className="tabela-resultados">
          <thead>
            <tr>
              <th rowSpan="2">CP Nº</th>
              <th colSpan="3">DIMENSÕES MÉDIAS (mm)</th>
              <th rowSpan="2">MASSA<br />(g)</th>
              <th rowSpan="2">Carga de<br />ruptura<br />Tf</th>
              <th rowSpan="2">Força de<br />ruptura<br />(kN)</th>
              <th rowSpan="2">Fator<br />Multiplicador<br />(p)</th>
              <th rowSpan="2">Resistência<br />Ruptura<br />(MPa)</th>
            </tr>
            <tr>
              <th>Largura<br />(L)</th>
              <th>Comprim.<br />(C)</th>
              <th>Altura<br />(H)</th>
            </tr>
          </thead>
          <tbody>
            {corposProva.map((item, index) => (
              <tr key={index}>
                <td>{item.cp || index + 1}</td>
                <td>{numeroBR(item.largura, 1)}</td>
                <td>{numeroBR(item.comprimento, 1)}</td>
                <td>{numeroBR(item.altura, 1)}</td>
                <td>{item.massa || "-"}</td>
                <td>{numeroBR(item.cargaRupturaTf, 2)}</td>
                <td>{numeroBR(item.forcaRupturaKn, 3)}</td>
                <td>{numeroBR(item.fatorMultiplicador || 1, 2)}</td>
                <td>{numeroBR(item.resistenciaRupturaMpa, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="resumo-grid">
          <div className="area-box">
            <div>Área do corpo de prova de Diâmetro de {diametroCorpoProva} mm</div>
            <div className="area-valor">{numeroBR(ensaio.areaCorpoProva || AREA_PADRAO_PAVER_MM2, 1)} mm²</div>
            <div>
              <em>Valor considerado para o Newton = 9,806</em>
            </div>
          </div>
          <div className="estatistica-box">
            <div>Valor Médio</div>
            <div>(MPa)</div>
            <div>
              <strong>{numeroBR(resumo.media, 1)}</strong>
            </div>
            <div>Desvio Padrão</div>
            <div>(MPa)</div>
            <div>{numeroBR(resumo.desvioPadrao, 2)}</div>
            <div>Coeficiente de <em>Student</em></div>
            <div>(t)</div>
            <div>{numeroBR(resumo.coeficienteStudent, 3)}</div>
            <div>f<sub>pk</sub> , estimado</div>
            <div>(MPa)</div>
            <div>
              <strong>{numeroBR(resumo.fpkEstimado, 1)}</strong>
            </div>
          </div>
        </div>

        <LinhaFinalPaginaPaver />
      </div>

      <div className="pagina pagina-complemento">
        <CabecalhoRelatorioPaver dataRelatorio={dataRelatorio} codigo={ensaio.codigo} />

        <div className="secao">5. COMENTÁRIOS ADVERSOS</div>
        <div className="comentario">
          {valor(
            ensaio.comentarios,
            "Os resultados de ensaio(s) e as considerações feitas neste relatório têm significado restrito às amostras analisadas e às condições em que os ensaios foram executados."
          )}
        </div>

        <div className="secao">6. RESULTADO</div>
        <div className="comentario">
          {temFpkEstimado ? (
            <>
              O resultado de f<sub>pk</sub>, estimado obtido foi de&nbsp;&nbsp;
              <strong>{numeroBR(resumo.fpkEstimado, 1)} Mpa</strong>
            </>
          ) : (
            <>
              O resultado médio obtido foi de&nbsp;&nbsp;
              <strong>{numeroBR(resumo.media, 1)} Mpa</strong>
            </>
          )}
          {temIdade && (
            <>
              &nbsp;&nbsp;aos&nbsp;&nbsp;
              <strong>{idade}</strong>
              &nbsp;&nbsp;dias de idade
            </>
          )}
          .
        </div>

        <div className="secao">7. OBSERVAÇÃO</div>
        <div className="comentario">
          {valor(
            ensaio.observacao,
            temIdade ? `Lote com ${idade} dias de idade.` : "-"
          )}
        </div>

        <div className="secao">8. ANEXOS</div>
        <div className="comentario">ANEXO 1 - Imagens das rupturas dos Pavers</div>
        {ensaio.prensaRuptura && (
          <div className="comentario">
            ANEXO 2 - Certificado de Calibração - prensa de concreto
          </div>
        )}

        <AssinaturaResponsavelTecnico />
        <LinhaFinalPaginaPaver />
      </div>

      <RelatorioFotografico ensaio={ensaio} fotos={fotosRelatorio} />

      {ensaio.prensaRuptura && (
        <div className="certificado-prensa-anexo">
          <RelatorioCalibracaoPrensaPrint dados={ensaio.prensaRuptura} />
        </div>
      )}
    </div>
  );
}
